import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { ReactFlowProvider, useReactFlow } from "reactflow";
import { useLocation, useNavigate } from "react-router-dom";

import DragSidebar from "./DragSidebar";
import FlowEditor from "./FlowEditor";
import CanvasHeader from "./CanvasHeader";
import TabBar from "./TabBar";
import { DiagramSettingsProvider } from "./DiagramSettingsContext";
import WorkspacePanel from "../Dashboard/WorkspacePanel";
import DashboardLayout from "../Dashboard/DashboardLayout";
import VersionHistory from "./VersionHistory";
import SaveVersionModal from "./SaveVersionModal";
import DocumentationModal from "../AI/DocumentationModal";
import { Node, Edge } from "reactflow";
import { updateArchitecture, getArchitecture } from "../../api/architectures";
import { logActivity } from "../../api/activity";
import { getProject } from "../../api/projects";
import { createVersion } from "../../api/versioning";
import { ArchitectureTabsProvider, useArchitectureTabs } from "../../context/ArchitectureTabsContext";
import { useAuth } from "../../context/AuthContext";
import { useCostEstimation } from "../../hooks/useCostEstimation";
import { CostEstimationPanel } from "./CostEstimationPanel";
import AIArchitectureGenerator from "../AI/AIArchitectureGenerator";
import TerraformModal from "../AI/TerraformModal";
import ReviewModal from "../AI/ReviewModal";
import FinOpsModal from "../AI/FinOpsModal";
import CompareVariantsModal from "../AI/CompareVariantsModal";
import ChatModal from "../AI/ChatModal";
import MarkdownViewerModal from "../AI/MarkdownViewerModal";
import ComplianceModal from "../AI/ComplianceModal";
import MigrationAssistantModal from "../AI/MigrationAssistantModal";
import PresalesModal from "../AI/PresalesModal";
import IncrementalUpdateModal from "../AI/IncrementalUpdateModal";
import DriftDetectionModal from "../AI/DriftDetectionModal";
import ContinuousOptimizationModal from "../AI/ContinuousOptimizationModal";
import { FiFileText, FiCode } from 'react-icons/fi';
import { generateRunbook, generateAdr } from "../../api/ai";

interface CanvasContentProps {
  project: any;
  nodes: any[];
  edges: any[];
  onLoadProject: (project: any) => void;
  onNodesEdgesChange?: (nodes: Node[], edges: Edge[]) => void;
}

const CanvasContent: React.FC<CanvasContentProps> = ({ project, nodes, edges, onLoadProject, onNodesEdgesChange }) => {
  const { t } = useTranslation('canvas');
  const rf = useReactFlow();
  const navigate = useNavigate();
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showTerraformModal, setShowTerraformModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showReviewModal, setShowReviewModal]         = useState(false);
  const [showFinOpsModal, setShowFinOpsModal]         = useState(false);
  const [showVariantsModal, setShowVariantsModal]     = useState(false);
  const [showRunbookModal, setShowRunbookModal]       = useState(false);
  const [showAdrModal, setShowAdrModal]               = useState(false);
  const [showComplianceModal, setShowComplianceModal]   = useState(false);
  const [showMigrationModal, setShowMigrationModal]     = useState(false);
  const [showPresalesModal, setShowPresalesModal]         = useState(false);
  const [showIncrementalModal, setShowIncrementalModal]   = useState(false);
  const [prevSnapshot, setPrevSnapshot]                   = useState<{ nodes: any[]; edges: any[] } | null>(null);
  const [showOptimizeModal, setShowOptimizeModal]         = useState(false);
  const [showDriftModal, setShowDriftModal]               = useState(false);
  const [showChatModal, setShowChatModal]             = useState(false);
  const [successMessage, setSuccessMessage]           = useState<string | null>(null);
  const { tabs, activeTabId, markTabDirty, getActiveTab } = useArchitectureTabs();
  const { user } = useAuth();

  // Helper pour générer la clé localStorage avec isolation par utilisateur
  const getArchStorageKey = useCallback((architectureId: string): string => {
    if (!user?.id) return `architecture.${architectureId}.canvas`; // Fallback
    return `architecture.${user.id}.${architectureId}.canvas`;
  }, [user?.id]);

  // Cost estimation hook - pass architectureId for variable resolution
  const costEstimation = useCostEstimation(rf.getNodes(), project?.currency || 'USD', 1000, project?.architectureId);

  /** 🧭 Charger depuis fichier JSON */
  const handleLoad = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      try {
        const data = JSON.parse(text);
        rf.setNodes(data.nodes || []);
        rf.setEdges(data.edges || []);
        alert(t('canvasWrapper.messages.projectLoaded'));
      } catch {
        alert(t('canvasWrapper.messages.invalidJson'));
      }
    };
    input.click();
  };

  /** 💾 Sauvegarder */
  const handleSave = async () => {
    // Récupérer les données actuelles du tab context (plus fiable)
    const activeTab = tabs.find(t => t.id === activeTabId);
    const currentNodes = activeTab?.nodes || rf.getNodes();
    const currentEdges = activeTab?.edges || rf.getEdges();

    const diagramData = {
      nodes: currentNodes,
      edges: currentEdges,
    };

    console.log('💾 Données à sauvegarder:', diagramData.nodes.length, 'nodes,', diagramData.edges.length, 'edges');

    // Si on a un architectureId, sauvegarder via l'API (qui passe par ADLS)
    if (project?.architectureId) {
      try {
        console.log('💾 Sauvegarde de l\'architecture via API/ADLS...', project.architectureId);

        await updateArchitecture(project.architectureId, {
          diagramData: diagramData
        });

        // Sauvegarder aussi en local pour le cache
        const archKey = getArchStorageKey(project.architectureId);
        localStorage.setItem(archKey, JSON.stringify(diagramData));

        // Marquer l'onglet comme sauvegardé (pas dirty)
        if (activeTabId) {
          markTabDirty(activeTabId, false);
        }

        alert(t('canvasWrapper.messages.architectureSaved'));
      } catch (error: any) {
        console.error('❌ Erreur lors de la sauvegarde:', error);
        alert(t('canvasWrapper.messages.saveError', { error: error.response?.data?.error || error.message }));
      }
    } else {
      // Fallback: sauvegarde locale seulement si pas d'architecture ID
      const projectData = {
        id: Date.now(),
        ...project,
        date: new Date().toISOString(),
        nodes: currentNodes,
        edges: currentEdges,
      };

      const existing = JSON.parse(localStorage.getItem("projects.saved") || "[]");
      const updated = [...existing, projectData];
      localStorage.setItem("projects.saved", JSON.stringify(updated));

      alert(t('canvasWrapper.messages.projectSavedLocally'));
    }
  };

  // Bouton "Sauvegarder" rapide depuis la toolbar FlowEditor (custom event)
  // Ref pour éviter de re-enregistrer l'écouteur à chaque render
  const handleSaveRef = React.useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    const handler = () => handleSaveRef.current();
    window.addEventListener('canvas-save', handler);
    return () => window.removeEventListener('canvas-save', handler);
  }, []);

  /** ⬇️ Exporter vers la machine */
  const handleExport = () => {
    const data = {
      project,
      nodes: rf.getNodes(),
      edges: rf.getEdges(),
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${project.name || "architecture"}.json`;
    a.click();
  };

  /** 🔄 Réinitialiser */
  const handleReset = () => {
    rf.setNodes([]);
    rf.setEdges([]);
  };

  /** 🔍 Fit View */
  const handleFitView = () => {
    rf.fitView({ padding: 0.2 });
  };

  /** 📄 Générer la documentation */
  const handleGenerateDoc = () => {
    setShowDocModal(true);
  };

  /** 🔖 Créer une version */
  const handleSaveVersion = () => {
    setShowSaveVersionModal(true);
  };

  const handleSaveVersionConfirm = async (version: string, description: string) => {
    try {
      // Obtenir l'ID de l'architecture active
      const architectureId = activeTabId;

      if (!architectureId) {
        console.error('No active architecture');
        setSuccessMessage('Erreur: Aucune architecture active');
        setTimeout(() => setSuccessMessage(null), 5000);
        return;
      }

      // D'abord sauvegarder le Canvas dans la base de données
      const currentNodes = rf.getNodes();
      const currentEdges = rf.getEdges();

      console.log('💾 Saving canvas before creating version:', currentNodes.length, 'nodes,', currentEdges.length, 'edges');

      // Vérifier que le Canvas n'est pas vide
      if (currentNodes.length === 0) {
        setSuccessMessage('⚠️ Impossible de créer une version: Aucune ressource dans le Canvas. Veuillez ajouter des ressources depuis la barre latérale.');
        setTimeout(() => setSuccessMessage(null), 7000);
        return;
      }

      const diagramData = {
        nodes: currentNodes,
        edges: currentEdges
      };

      // Sauvegarder le Canvas dans la base de données
      await updateArchitecture(architectureId, {
        diagramData
      });

      console.log('✅ Canvas saved, now creating version...');

      // Appeler l'API pour créer une version en passant directement les diagram_data
      // pour éviter les problèmes de synchronisation avec la base de données
      await createVersion(architectureId, {
        terraform_code: { main: '', variables: '', outputs: '' }, // Sera généré par le backend
        variables: {},  // Sera récupéré par le backend
        commit_message: description || version,
        diagram_data: diagramData,  // Passer les données directement
      });

      // Toujours sauvegarder dans localStorage pour l'ancien système

      const versionData = {
        id: Date.now(),
        projectId: project.id,
        projectName: project.projectName || project.name,
        architectureId: architectureId,
        architectureName: project.name,
        version: version,
        description: description,
        nodes: currentNodes,
        edges: currentEdges,
        createdAt: new Date().toISOString(),
      };

      const existing = JSON.parse(localStorage.getItem("project.versions") || "[]");
      const updated = [...existing, versionData];
      localStorage.setItem("project.versions", JSON.stringify(updated));

      // Afficher le message de succès
      setSuccessMessage(t('canvasWrapper.messages.versionCreated', { version: versionData.version }));

      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error: any) {
      console.error('Failed to create version:', error);
      setSuccessMessage(`Erreur: ${error.response?.data?.error || error.message}`);
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    }
  };

  /** 📜 Voir l'historique des versions */
  const handleViewVersionHistory = () => {
    setShowVersionHistory(true);
  };

  /** 🔄 Charger une version */
  const handleLoadVersion = (versionNodes: Node[], versionEdges: Edge[]) => {
    rf.setNodes(versionNodes);
    rf.setEdges(versionEdges);
    rf.fitView({ padding: 0.3 });
  };

  /** ✅ Recharger les nodes/edges quand l'architecture change (pas à chaque modification) */
  const lastLoadedArchIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Ne faire fitView que lors du chargement initial d'une architecture
    const archId = project?.architectureId;
    if (archId && archId !== lastLoadedArchIdRef.current) {
      lastLoadedArchIdRef.current = archId;
      // Tracer la consultation (fire & forget)
      logActivity('architecture', archId);
      if (nodes?.length || edges?.length) {
        rf.setNodes(nodes);
        rf.setEdges(edges);
        rf.fitView({ padding: 0.3 });
      }
    }
  }, [nodes, edges, project?.architectureId]);

  // Handler pour nouvelle architecture
  const handleNewArchitecture = () => {
    navigate('/projects');
  };

  // Récupérer l'onglet actif pour afficher les bonnes informations
  const activeTab = getActiveTab();

  // Créer l'objet project à partir de l'onglet actif (ou fallback sur project)
  const displayProject = activeTab ? {
    projectName: activeTab.projectName,
    name: activeTab.name,
    provider: activeTab.provider,
    architectureType: activeTab.architectureType || 'Standard'
  } : project;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Barre d'onglets */}
      {tabs.length > 0 && (
        <TabBar onNewArchitecture={handleNewArchitecture} />
      )}

      <CanvasHeader
        project={displayProject}
        onSave={handleSave}
        onLoad={handleLoad}
        onExport={handleExport}
        onReset={handleReset}
        onFitView={handleFitView}
        onGenerateDoc={handleGenerateDoc}
        onSaveVersion={handleSaveVersion}
        onViewVersionHistory={handleViewVersionHistory}
        onGenerateWithAI={() => setShowAIModal(true)}
        onGenerateTerraform={() => setShowTerraformModal(true)}
        onReviewWithAI={() => setShowReviewModal(true)}
        onFinOpsWithAI={() => setShowFinOpsModal(true)}
        onCompareVariants={() => setShowVariantsModal(true)}
        onGenerateRunbook={() => setShowRunbookModal(true)}
        onGenerateAdr={() => setShowAdrModal(true)}
        onChatWithInfra={() => setShowChatModal(true)}
        onScanCompliance={() => setShowComplianceModal(true)}
        onMigrationAssistant={() => setShowMigrationModal(true)}
        onPresalesAssistant={() => setShowPresalesModal(true)}
        onIncrementalUpdate={() => {
          setPrevSnapshot({ nodes: rf.getNodes(), edges: rf.getEdges() });
          setShowIncrementalModal(true);
        }}
        onDriftDetection={() => setShowDriftModal(true)}
        onContinuousOptimization={() => setShowOptimizeModal(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <DragSidebar />
        <FlowEditor />
      </div>

      {showVersionHistory && activeTabId && (
        <VersionHistory
          architectureId={activeTabId}
          architectureName={project.name}
          onLoadVersion={handleLoadVersion}
          onClose={() => setShowVersionHistory(false)}
        />
      )}

      {/* Save Version Modal */}
      <SaveVersionModal
        isOpen={showSaveVersionModal}
        onClose={() => setShowSaveVersionModal(false)}
        onSave={handleSaveVersionConfirm}
      />

      {/* Success Toast Notification */}
      {successMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#10b981',
            color: '#fff',
            padding: '16px 24px',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 1000,
            animation: 'slideInUp 0.3s ease-out',
            maxWidth: 400,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 500 }}>{successMessage}</span>
        </div>
      )}

      {/* AI Architecture Generator Modal */}
      <AIArchitectureGenerator
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        architectureId={project?.architectureId}
        initialDiagram={{ nodes: rf.getNodes(), edges: rf.getEdges() }}
        onApply={(data) => {
          rf.setNodes(data.nodes);
          rf.setEdges(data.edges);
          setTimeout(() => rf.fitView({ padding: 0.2 }), 50);
        }}
      />

      {/* Terraform Generator Modal */}
      <TerraformModal
        isOpen={showTerraformModal}
        onClose={() => setShowTerraformModal(false)}
        nodes={rf.getNodes()}
        edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        projectName={project?.name}
      />

      {/* AI Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        nodes={rf.getNodes()}
        edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        architectureId={project?.architectureId}
      />

      {/* FinOps Modal */}
      <FinOpsModal
        isOpen={showFinOpsModal}
        onClose={() => setShowFinOpsModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        costs={costEstimation}
        architectureId={project?.architectureId}
      />

      {/* Compare Variants Modal */}
      <CompareVariantsModal
        isOpen={showVariantsModal}
        onClose={() => setShowVariantsModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        architectureId={project?.architectureId}
      />

      {/* Runbook Modal */}
      <MarkdownViewerModal
        isOpen={showRunbookModal}
        onClose={() => setShowRunbookModal(false)}
        title="Runbook Opérationnel"
        icon={<FiFileText size={18} className="text-orange-600" />}
        iconBg="bg-orange-100"
        filename={`runbook-${project?.name || 'architecture'}.md`}
        architectureId={project?.architectureId}
        cacheType="runbook"
        fetchContent={async () => {
          const r = await generateRunbook({
            nodes: rf.getNodes(), edges: rf.getEdges(),
            provider: (project?.provider || 'azure').toLowerCase(),
            environment: 'production',
            architectureName: project?.name,
          });
          return { content: r.runbook, metadata: r.metadata };
        }}
      />

      {/* ADR Modal */}
      <MarkdownViewerModal
        isOpen={showAdrModal}
        onClose={() => setShowAdrModal(false)}
        title="Architecture Decision Records"
        icon={<FiCode size={18} className="text-sky-600" />}
        iconBg="bg-sky-100"
        filename={`adr-${project?.name || 'architecture'}.md`}
        architectureId={project?.architectureId}
        cacheType="adr"
        fetchContent={async () => {
          const r = await generateAdr({
            nodes: rf.getNodes(), edges: rf.getEdges(),
            provider: (project?.provider || 'azure').toLowerCase(),
            environment: 'production',
            architectureName: project?.name,
          });
          return { content: r.adrDoc, metadata: r.metadata };
        }}
      />

      {/* Chat Infrastructure Modal */}
      <ChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        projectName={project?.projectName}
      />

      {/* Compliance Scanner Modal */}
      <ComplianceModal
        isOpen={showComplianceModal}
        onClose={() => setShowComplianceModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        architectureId={project?.architectureId}
      />

      {/* Incremental Update Modal */}
      <IncrementalUpdateModal
        isOpen={showIncrementalModal}
        onClose={() => setShowIncrementalModal(false)}
        currentNodes={rf.getNodes()} currentEdges={rf.getEdges()}
        previousNodes={prevSnapshot?.nodes} previousEdges={prevSnapshot?.edges}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
      />

      {/* Presales Modal */}
      <PresalesModal
        isOpen={showPresalesModal}
        onClose={() => setShowPresalesModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        projectName={project?.projectName}
        costs={costEstimation}
      />

      {/* Migration Assistant Modal */}
      <MigrationAssistantModal
        isOpen={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        architectureId={project?.architectureId}
        currentNodes={rf.getNodes()}
        onApplyNodes={(newNodes, newEdges) => {
          rf.setNodes([...rf.getNodes(), ...newNodes]);
          rf.setEdges([...rf.getEdges(), ...newEdges]);
        }}
      />

      {/* Continuous Optimization Modal */}
      <ContinuousOptimizationModal
        isOpen={showOptimizeModal}
        onClose={() => setShowOptimizeModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        architectureId={project?.architectureId}
        costs={costEstimation}
      />

      {/* Drift Detection Modal */}
      <DriftDetectionModal
        isOpen={showDriftModal}
        onClose={() => setShowDriftModal(false)}
        nodes={rf.getNodes()} edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
      />

      {/* AI Documentation Modal */}
      <DocumentationModal
        isOpen={showDocModal}
        onClose={() => setShowDocModal(false)}
        architectureId={activeTabId || undefined}
        nodes={rf.getNodes()}
        edges={rf.getEdges()}
        provider={(project?.provider || 'azure').toLowerCase()}
        environment="production"
        architectureName={project?.name}
        projectName={project?.projectName}
        costs={costEstimation}
      />

      {/* Cost Estimation Panel */}
      <CostEstimationPanel
        totalMonthly={costEstimation.totalMonthly || 0}
        totalHourly={costEstimation.totalHourly || 0}
        currency={costEstimation.currency || 'USD'}
        breakdown={costEstimation.breakdown || []}
        unsupported={costEstimation.unsupported || []}
        loading={costEstimation.loading}
        error={costEstimation.error}
        updatedAt={costEstimation.updatedAt}
        disclaimer={costEstimation.disclaimer}
        onRefresh={costEstimation.refresh}
        resources={rf.getNodes().filter(n => n.type !== 'annotation' && n.data?.tfKey).map(node => ({
          id: node.id,
          type: node.data.tfKey || node.data.resourceType || '',
          params: {
            ...node.data.params,
            ...node.data.config,
            name: node.data.label || node.data.name,
            label: node.data.label
          }
        }))}
        projectName={project?.name || project?.title || t('canvasWrapper.defaults.architectureCloud')}
      />

      {/* Animations CSS */}
      <style>
        {`
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

// Inner component that uses tabs context
const CanvasWrapperInner: React.FC = () => {
  const { t } = useTranslation('canvas');
  const location = useLocation();
  const [project, setProject] = useState<any>(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const { user } = useAuth();

  const { openTab, tabs, activeTabId, getActiveTab, updateTabData } = useArchitectureTabs();

  // Helper pour générer la clé localStorage avec isolation par utilisateur
  const getArchStorageKey = useCallback((architectureId: string): string => {
    if (!user?.id) return `architecture.${architectureId}.canvas`; // Fallback
    return `architecture.${user.id}.${architectureId}.canvas`;
  }, [user?.id]);

  /** Load project from navigation state or URL query params or localStorage */
  useEffect(() => {
    const loadProjectData = async () => {
      const state = location.state as any;

      // Lire aussi les query parameters de l'URL (pour l'ouverture dans nouvel onglet navigateur)
      const searchParams = new URLSearchParams(location.search);
      const urlProjectId = searchParams.get('projectId');
      const urlArchitectureId = searchParams.get('architectureId');

      // Priorité : state > URL params
      const projectId = state?.projectId || urlProjectId;
      const architectureId = state?.architectureId || urlArchitectureId;

      if (projectId && architectureId) {
        try {
          // Charger les vraies données depuis l'API
          console.log('📡 Chargement du projet et de l\'architecture depuis l\'API...');

          const [projectData, archData] = await Promise.all([
            getProject(projectId),
            getArchitecture(architectureId)
          ]);

          console.log('✅ Projet chargé:', projectData);
          console.log('✅ Architecture chargée:', archData);

          // Ouvrir l'architecture dans un onglet avec les données du projet
          await openTab(
            architectureId,
            projectId,
            projectData.name || t('canvasWrapper.defaults.project'),
            'Standard'
          );

          const projectInfo = {
            id: projectId,
            projectName: projectData.name || t('canvasWrapper.defaults.project'),
            name: archData.name || t('canvasWrapper.defaults.architecture'),
            description: archData.description || '',
            provider: archData.provider || 'Azure',
            architectureType: 'Standard',
            architectureId: architectureId,
            architectureData: archData
          };

          setProject(projectInfo);

          // Charger le diagramme depuis l'architecture
          if (archData.diagram_data) {
            setNodes(archData.diagram_data.nodes || []);
            setEdges(archData.diagram_data.edges || []);
          }

        } catch (error) {
          console.error('❌ Erreur lors du chargement:', error);

          // Fallback sur les données du state ou valeurs par défaut
          const projectInfo = {
            id: projectId,
            projectName: state?.projectName || t('canvasWrapper.defaults.project'),
            name: state?.architectureName || state?.architectureData?.name || t('canvasWrapper.defaults.architecture'),
            description: state?.architectureData?.description || '',
            provider: state?.architectureData?.provider || 'Azure',
            architectureType: state?.architectureData?.type || state?.architectureType || 'Standard',
            architectureId: architectureId,
            architectureData: state?.architectureData
          };
          setProject(projectInfo);

          // Fallback localStorage
          const archKey = getArchStorageKey(architectureId);
          const savedCanvas = localStorage.getItem(archKey);
          if (savedCanvas) {
            const canvasData = JSON.parse(savedCanvas);
            setNodes(canvasData.nodes || []);
            setEdges(canvasData.edges || []);
          }
        }
      } else if (projectId) {
        // Projet sans architecture (nouveau canvas)
        const projectInfo = {
          id: projectId,
          projectName: state?.projectName || t('canvasWrapper.defaults.project'),
          name: state?.architectureName || state?.architectureData?.name || t('canvasWrapper.defaults.architecture'),
          description: state?.architectureData?.description || '',
          provider: state?.architectureData?.provider || 'Azure',
          architectureType: state?.architectureData?.type || state?.architectureType || 'Standard',
          architectureId: architectureId,
          architectureData: state?.architectureData
        };
        setProject(projectInfo);
      } else {
        // Try to load from localStorage (legacy support)
        const saved = localStorage.getItem("project.current");
        if (saved) {
          setProject(JSON.parse(saved));
        }
      }
    };

    loadProjectData();
  }, [location]);

  // Synchroniser les données de l'onglet actif avec le state local
  const lastActiveTabIdRef = React.useRef<string | null>(null);
  const lastLoadingStateRef = React.useRef<boolean>(false);
  useEffect(() => {
    const activeTab = getActiveTab();

    const tabChanged = activeTabId !== lastActiveTabIdRef.current;
    const finishedLoading = activeTab && !activeTab.isLoading && lastLoadingStateRef.current === true;

    // Ne synchroniser que si l'onglet actif a changé OU si le chargement vient de terminer
    if (!tabChanged && !finishedLoading) return;

    lastActiveTabIdRef.current = activeTabId;
    lastLoadingStateRef.current = activeTab?.isLoading ?? false;

    if (activeTab && !activeTab.isLoading) {
      // Mettre à jour le projet avec les infos de l'onglet
      setProject((prev: any) => ({
        ...prev,
        id: activeTab.projectId,
        name: activeTab.name,
        provider: activeTab.provider,
        architectureId: activeTab.id,
      }));

      // Mettre à jour nodes/edges
      setNodes(activeTab.nodes as any);
      setEdges(activeTab.edges as any);
    }
  }, [activeTabId, tabs, getActiveTab]);

  /** 🔁 Ouverture d'un projet sauvegardé */
  const handleOpenProject = (proj: any) => {
    console.log("🔁 Projet ouvert :", proj);
    setProject({
      name: proj.name,
      description: proj.description || "",
      provider: proj.provider,
      architectureType: proj.architectureType,
    });
    setNodes(proj.nodes || []);
    setEdges(proj.edges || []);
    setShowWorkspace(false);
  };

  return (
    <DiagramSettingsProvider>
      {project && (
        <ReactFlowProvider>
          <div className="flex flex-col h-full bg-white text-black">
            <CanvasContent
              project={project}
              nodes={nodes}
              edges={edges}
              onLoadProject={handleOpenProject}
            />

            {showWorkspace && (
              <WorkspacePanel
                onClose={() => setShowWorkspace(false)}
                onOpenProject={handleOpenProject}
              />
            )}
          </div>
        </ReactFlowProvider>
      )}
    </DiagramSettingsProvider>
  );
};

// Main wrapper (ArchitectureTabsProvider is now at App level)
const CanvasWrapper: React.FC = () => {
  return (
    <DashboardLayout>
      <CanvasWrapperInner />
    </DashboardLayout>
  );
};

export default CanvasWrapper;
